import { Link } from "react-router";

import type { LockReason, SectionSlug } from "@/api/hooks";
import type { AdoptionPin, LibraryAsset } from "@/api/library";
import {
  isBehind,
  unlicensedRefusal,
  type AdoptionDecision,
} from "@/lib/adoptions";
import { ASSET_TYPE_LABELS, libraryAssetHref } from "@/lib/librarySections";
import type { QuestionnaireAdoptionDetail } from "@/lib/questionnaireAdoption";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { ReadOnlyField } from "@/ui/ReadOnlyField";
import { Tooltip } from "@/ui/Tooltip";

import { PinnedQuestionnaire } from "./PinnedQuestionnaire";

/**
 * The two adoption rows, prop-driven so they can be exercised from their own
 * props (#372).
 *
 * Separated from `ProjectAdoptions` for the reason `SectionFieldList` is
 * separated from `SectionDiagnostics`: what these rows render for a LOCKED
 * adoption is a ticket criterion, and the state that produces one — a
 * server-declared guard floor, or an engine-reported lock on the values a pin
 * supplied — is not reachable through the live component lane, which mounts no
 * projection engine and whose `library.create_asset` writes `locked_floor=False`
 * unconditionally. A prop-driven test asserts it directly instead of describing
 * a response no service in that lane produces.
 *
 * Neither component decides anything. Every verdict below — the lock, its
 * reason, whether a write is refused and why — arrives as a prop, computed in
 * `lib/adoptions.ts` from what the service reported.
 */

/**
 * One adoption this project holds.
 *
 * The pinned version is rendered through `ReadOnlyField` rather than as loose
 * text, and that is the criterion about locks rather than a styling choice: the
 * component owns ADR 0001's rule that locked content disables its controls —
 * the padlock, the `LockReason` label and detail, `aria-readonly` and
 * `aria-disabled` on the value, and the reason travelling with the field rather
 * than living in a hover-only tooltip. Reusing it is what keeps a locked
 * adoption and a locked field explaining themselves in the same words.
 *
 * TYPE-SPECIFIC DETAIL ARRIVES AS A PROP TOO (#369). A questionnaire row states
 * what the pinned instrument administers; every other type renders exactly what
 * it rendered before, because its caller passes `questionnaire: null`. The row
 * does not read the version, decide whether the asset is a questionnaire, or
 * classify a failed read — `lib/questionnaireAdoption.ts` does all three from
 * what the service returned, for the same reason the lock beside it is a prop.
 */
export function AdoptedRow({
  pin,
  asset,
  lock,
  suppliedSections,
  questionnaire = null,
  onRetryQuestionnaire,
  upgradeRefusal,
  unpinRefusal,
  onDecide,
}: {
  pin: AdoptionPin;
  /** Absent when the shared library did not return the pinned asset. */
  asset: LibraryAsset | undefined;
  /** The service's lock on this adoption, or `null`. Never re-derived here. */
  lock: LockReason | null;
  /** Sections whose effective values this adoption supplied, per the service. */
  suppliedSections: readonly SectionSlug[];
  /**
   * What the pinned version administers, or `null` for a row this ticket does
   * not touch — every non-questionnaire pin, and every pin the catalog could
   * not name.
   */
  questionnaire?: QuestionnaireAdoptionDetail | null;
  /** Re-reads the pinned version, or absent when no retry could clear it. */
  onRetryQuestionnaire?: () => void;
  upgradeRefusal: string | null;
  unpinRefusal: string | null;
  onDecide: (decision: AdoptionDecision) => void;
}) {
  const name = asset?.name ?? pin.assetId;
  const behind = isBehind(pin, asset);

  return (
    <li className="rounded-md border border-border px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        <div className="min-w-0 flex-1 space-y-2">
          <ReadOnlyField
            label={name}
            value={<span className="font-mono">{pin.version}</span>}
            {...(lock ? { lockReason: lock } : {})}
            descriptor={{
              label: `Pinned ${pin.version}`,
              tone: lock ? "warning" : "accent",
              // Never editable in place: the version changes by replacing the
              // whole pin set through the controls beside it, never by typing.
              editable: false,
              reason:
                "A pin names an immutable version. Move or remove it with the controls beside it; the value itself is not edited here.",
              locked: lock !== null,
            }}
          />
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {/* The pin's own family (#615): named whether or not the catalog answered. */}
            <Badge tone="outline">{ASSET_TYPE_LABELS[pin.assetType]}</Badge>
            {asset?.capabilities.archived ? (
              <Badge tone="neutral">Archived</Badge>
            ) : null}
            {behind && asset ? (
              <span>{asset.latestVersion} available in the library</span>
            ) : asset ? (
              <span>Pinned at the library&apos;s head version</span>
            ) : null}
            {suppliedSections.length > 0 ? (
              <span>Supplies {suppliedSections.join(", ")}</span>
            ) : null}
          </div>
          {questionnaire ? (
            <PinnedQuestionnaire
              name={name}
              version={pin.version}
              detail={questionnaire}
              behind={behind}
              {...(onRetryQuestionnaire
                ? { onRetry: onRetryQuestionnaire }
                : {})}
            />
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            The exact library page of the pinned asset (#615). Derived from the
            STORED pin alone — its `assetId` and the `assetType` the service
            joins onto it — never from the row's label or the section it
            renders in, and never from the catalog: this is the ticket's
            failure-state criterion, that the way back to the authoritative
            asset survives a catalog read that failed while the exact pin is
            known. Rendered whatever the write state too, because navigation
            to shared clinical content is read-only and gating it on a write
            capability would hide evidence. The name falls back to the id
            when the catalog has not named the pin, and says so.
          */}
          <Link
            to={libraryAssetHref(pin.assetType, pin.assetId)}
            className="wrap-anywhere text-sm font-medium text-accent-foreground underline underline-offset-4"
          >
            {`View ${name} in Clinical library`}
          </Link>
          {behind && asset ? (
            <Tooltip
              content={
                upgradeRefusal ??
                `Move this project's pin on ${name} from ${pin.version} to ${asset.latestVersion} in one replacement`
              }
            >
              <Button
                size="sm"
                disabled={upgradeRefusal !== null}
                onClick={() =>
                  onDecide({
                    mode: "pin",
                    assetId: pin.assetId,
                    version: asset.latestVersion,
                  })
                }
              >
                {`Update ${name} to ${asset.latestVersion}`}
              </Button>
            </Tooltip>
          ) : null}
          <Tooltip content={unpinRefusal ?? `Remove this project's pin on ${name}`}>
            <Button
              variant="outline"
              size="sm"
              disabled={unpinRefusal !== null}
              onClick={() => onDecide({ mode: "unpin", assetId: pin.assetId })}
            >
              {`Unpin ${name}`}
            </Button>
          </Tooltip>
        </div>
      </div>
    </li>
  );
}

/** One asset in the shared library this project does not adopt. */
export function AvailableRow({
  asset,
  refusal,
  onDecide,
}: {
  asset: LibraryAsset;
  refusal: string | null;
  onDecide: (decision: AdoptionDecision) => void;
}) {
  // The inventory verdict is rendered as TEXT, not only as the tooltip on a
  // disabled control (#638). A disabled button is not focusable, so its tooltip
  // is unreachable by keyboard and invisible to a screen reader — which for the
  // archived case is a pre-existing gap and for this one would be the whole
  // answer: "unavailable licence" is a state the ticket requires to be
  // explicit, and a clinician who cannot read why an approved-looking
  // instrument refuses learns nothing from a greyed-out button.
  const unlicensed = unlicensedRefusal(asset);
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-sm">
      <span className="min-w-0 flex-1 font-medium text-foreground">
        {asset.name}
      </span>
      <Badge tone="outline">{ASSET_TYPE_LABELS[asset.assetType]}</Badge>
      {asset.capabilities.archived ? (
        <Badge tone="neutral">Archived</Badge>
      ) : null}
      {unlicensed ? <Badge tone="warning">Not licensed</Badge> : null}
      <span className="font-mono text-muted-foreground">
        {asset.latestVersion}
      </span>
      <Tooltip
        content={
          refusal ??
          `Pin ${asset.name} at ${asset.latestVersion} into this project`
        }
      >
        <Button
          variant="outline"
          size="sm"
          // The label carries the asset's NAME, which a clinician chose and
          // which can be long. `Button` sets `whitespace-nowrap`, so at 375px a
          // long name pushed this control past the viewport edge (#634's
          // narrow-width arm, with a realistic name). Let the label wrap inside
          // the control instead: the row already wraps its own items, and a
          // control that grows a line is reachable where one that overflows
          // is not.
          className="h-auto min-h-8 max-w-full whitespace-normal py-1 text-left"
          disabled={refusal !== null}
          onClick={() =>
            onDecide({
              mode: "pin",
              assetId: asset.id,
              version: asset.latestVersion,
            })
          }
        >
          {`Pin ${asset.name}`}
        </Button>
      </Tooltip>
      {unlicensed ? (
        <p className="basis-full text-xs text-muted-foreground">{unlicensed}</p>
      ) : null}
    </li>
  );
}
